import { useQuery } from '@apollo/client'
import { Loader } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { DepartmentSelect } from '@/components_deprecated/Select/DepartmentSelect'
import type { Tab } from '@/components_deprecated/Tabs'
import { TabPane, Tabs } from '@/components_deprecated/Tabs'
import { VEHICLE_DETAIL_QUERY } from '@/modules/vehicles'
import { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useBreadcrumbs, useParam, usePermissions } from '@/utils/hooks'
import {
  VehicleDetails,
  VehicleDocuments,
  VehicleExpirations,
  VehicleNotes,
} from '../components'
import { useVehicleMutations } from '../mutations.hooks'
import { FacilitySelect } from '@/components_deprecated/Select/FacilitySelect'
import { Field } from '@/modules/companies'
import { PERMISSIONS } from '@/utils/permissions'
import { H2 } from '@/components'

const Wrapper = styled.div`
  ${props => props.theme.layout.default};
`

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
  padding-bottom: 1rem;
`

const SelectWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1rem;
`

interface VehicleProps {}

export const Vehicle: React.FC<VehicleProps> = () => {
  const translations = useTranslate({
    loading: 'common.loading-ellipsis',
    details: 'vehicles.vehicle-details',
    documents: 'common.documents',
    expirationDates: 'vehicles.expiration-dates',
    department: 'common.department',
    facility: 'common.facility',
  })

  const vehicleId = useParam('vehicleId')

  const { setOverride } = useBreadcrumbs()

  const { data, loading } = useQuery(VEHICLE_DETAIL_QUERY, {
    variables: {
      id: vehicleId,
    },
    onCompleted(data) {
      if (!data) return
      setOverride(vehicleId, data.vehicle.registrationNumber)
      updateForm({
        departmentId: data.vehicle.department?.id ?? '',
        facilityId: data.vehicle.facility?.id ?? '',
      })
    },
  })

  const { formValues: form, updateForm } = useForm<{
    departmentId: string | null
    facilityId: string | null
  }>({
    values: {
      departmentId: '',
      facilityId: '',
    },
  })

  const { hasPermissions } = usePermissions()

  const canEdit = hasPermissions(PERMISSIONS.vehicles.change.vehicle, true)

  const canViewDocuments = hasPermissions(
    PERMISSIONS.vehicles.view.vehicledocument,
    true
  )
  const canViewExpirations = hasPermissions(
    PERMISSIONS.vehicles.view.vehicleexpiration,
    true
  )

  const vehicle = data?.vehicle || null
  useEffect(() => {
    if (data) return
    setOverride(vehicleId, translations.loading)
  })

  const {
    mutations: { patch: patchVehicle },
  } = useVehicleMutations()

  async function handleDepartmentChange(departmentId: string | null) {
    updateForm({ departmentId })
    await patchVehicle({
      variables: {
        id: vehicleId,
        input: { department: departmentId ?? null },
      },
    })
  }

  async function handleFacilityChange(facilityId: string | null) {
    updateForm({ facilityId })
    await patchVehicle({
      variables: {
        id: vehicleId,
        input: { facility: facilityId ?? null },
      },
    })
  }

  const tabs = useMemo<Tab[]>(() => {
    const result: Tab[] = [{ key: 'details', text: translations.details }]

    if (canViewDocuments) {
      result.push({ key: 'documents', text: translations.documents })
    }

    if (canViewExpirations) {
      result.push({ key: 'expirations', text: translations.expirationDates })
    }

    return result
  }, [
    translations.details,
    translations.documents,
    translations.expirationDates,
    canViewDocuments,
    canViewExpirations,
  ])

  const [activeTab, setActiveTab] = useState<Tab>(tabs[0]!)

  return (
    <Wrapper>
      <TitleWrapper>
        <H2>{loading ? translations.loading : vehicle?.registrationNumber}</H2>
        {loading || (!vehicle && <Loader.Dots size={28} />)}
      </TitleWrapper>
      {!!vehicle && (
        <>
          <Tabs
            activeTab={activeTab}
            tabs={tabs}
            onChange={tab => setActiveTab(tab)}
          />

          <TabPane active={activeTab.key === 'details'}>
            <H2>{translations.details}</H2>
            {!loading && vehicle && (
              <SelectWrapper>
                <Field>
                  <label>{translations.department}</label>
                  <DepartmentSelect
                    height="3rem"
                    value={form.departmentId}
                    onChange={departmentId =>
                      handleDepartmentChange(departmentId)
                    }
                    searchable={true}
                    nullable={canEdit}
                    disabled={!canEdit}
                  />
                </Field>
                <Field>
                  <label>{translations.facility}</label>
                  <FacilitySelect
                    height="3rem"
                    value={form.facilityId}
                    onChange={facilityId => handleFacilityChange(facilityId)}
                    searchable={true}
                    nullable={canEdit}
                    disabled={!canEdit}
                  />
                </Field>
              </SelectWrapper>
            )}
            <VehicleDetails vehicle={vehicle} />
            <VehicleNotes vehicle={vehicle} />
          </TabPane>

          {canViewDocuments && (
            <TabPane active={activeTab.key === 'documents'}>
              <VehicleDocuments vehicleId={vehicleId} />
            </TabPane>
          )}
          {canViewExpirations && (
            <TabPane active={activeTab.key === 'expirations'}>
              <VehicleExpirations vehicleId={vehicleId} />
            </TabPane>
          )}
        </>
      )}
    </Wrapper>
  )
}
